import { Textarea } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Flex } from '@/components/ui/flex'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { childItemsAtom, rejectReasonForAllAtom, selectedOptionForAllAtom } from '@/store/tasksApprovals/detailsDrawer/sapGrcRoleAccess'
import { IApprovalDetail, IApprovalDetailChildItem } from '@/types/tasksApprovals'
import { deepClone } from '@/utils'

import ChildItem from './components/childItem/index'

const Container = ({ data, itemDetails }: { data: IApprovalDetailChildItem[]; itemDetails: IApprovalDetail }) => {
    const { t } = useTranslation()
    const [childItems, setChildItems] = useAtom(childItemsAtom)
    const [, setApproveAll] = useState(false)
    const [isRejectAll, setRejectAll] = useState(false)
    const [rejectReasonForAll, setRejectReasonForAll] = useAtom(rejectReasonForAllAtom)
    const [, setAllSelected] = useAtom(selectedOptionForAllAtom)

    const canTakeAction = itemDetails && typeof itemDetails?.approval_info?.can_be_approved === 'undefined'
    const borderColor = useColorModeValue('var(--chakra-colors-gray-300)', 'var(--chakra-colors-surface-default)')

    const updateData = (id: string | null, approve: boolean, reject: boolean, updateAll = false) => {
        return childItems.map((item) => {
            if (updateAll || item.approval_entity_id === id) {
                return {
                    ...item,
                    isApproved: approve !== undefined ? approve : item.isApproved,
                    isRejected: reject !== undefined ? reject : item.isRejected,
                }
            }
            return item
        })
    }

    const approveAll = () => {
        if (!canTakeAction) return
        setChildItems(updateData(null, true, false, true))
        setApproveAll(true)
        setRejectAll(false)
        setAllSelected('approve')
    }

    const rejectAll = () => {
        if (!canTakeAction) return
        setChildItems(updateData(null, false, true, true))
        setRejectAll(true)
        setApproveAll(false)
        setAllSelected('reject')
    }

    const onApprove = (id: string) => {
        setChildItems(updateData(id, true, false))
        setRejectAll(false)
    }

    const onReject = (id: string) => {
        setChildItems(updateData(id, false, true))
        setApproveAll(false)
    }

    const setRejectReason = (reason: string, entityId: string) => {
        let _data = [...childItems]

        _data = _data.map((item) => {
            if (item.approval_entity_id === entityId) {
                item.rejectReason = reason
            }

            return item
        })

        setChildItems(_data)
    }

    useEffect(() => {
        // Cleanup childItemsAtom
        return () => {
            setChildItems([])
            setRejectReasonForAll('')
            setAllSelected(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (childItems.length === 0) {
            // Add extra keys for internal logic
            const x = deepClone(data)
            x.map((item) => {
                item.isApproved = false
                item.isRejected = false
                item.rejectReason = ''
                item.isDisabled = itemDetails.approval_info?.can_be_approved === false
                return item
            })
            setChildItems(x)
        } else {
            if (childItems.every((item) => item.isApproved)) setAllSelected('approve')
            else if (childItems.every((item) => item.isRejected)) {
                setAllSelected('reject')
                setRejectAll(true)
            } else if (childItems.every((item) => item.isRejected || item.isApproved)) setAllSelected('mixed')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childItems])

    useEffect(() => {
        let _data = [...childItems]

        _data = _data.map((item) => {
            item.rejectReason = rejectReasonForAll

            return item
        })

        setChildItems(_data)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rejectReasonForAll])

    return (
        childItems.length > 0 && (
            <Box>
                <Box px="24px" width="100%">
                    <Flex
                        fontSize="12px"
                        borderY="1px solid"
                        borderColor={borderColor}
                        py="12px"
                        mb="12px"
                        alignItems={['self-start', 'self-start', 'center', 'center']}
                        flexDir={['column', 'column', 'row', 'row']}
                    >
                        <Flex
                            width={['calc(100%)', 'calc(100%)', 'calc(70%)', 'calc(70%)']}
                            gap="2%"
                            fontSize="14px"
                            justifyContent="space-between"
                        >
                            <Box width="100%" maxW={['calc(33%)', 'calc(33%)', 'calc(33%)', 'calc(33%)']}>
                                <Text className="ellipsis" fontWeight="bold" textTransform="uppercase">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.technicalName')}
                                </Text>
                            </Box>
                            <Box width="100%" maxW={['calc(37%)', 'calc(37%)', 'calc(37%)', 'calc(37%)']}>
                                <Text className="ellipsis" fontWeight="bold" textTransform="uppercase">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.accessDescription')}
                                </Text>
                            </Box>
                            <Box width="100%" maxW={['calc(13%)', 'calc(13%)', 'calc(13%)', 'calc(13%)']}>
                                <Text className="ellipsis" fontWeight="bold" textTransform="uppercase">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.action')}
                                </Text>
                            </Box>
                            <Box width="100%" maxW={['calc(8%)', 'calc(8%)', 'calc(8%)', 'calc(8%)']}></Box>
                        </Flex>
                        <Flex
                            flexGrow="1"
                            justifyContent={['flex-end', 'flex-end', 'space-around', 'space-around']}
                            pt={['8px', '8px', '0', '0']}
                            width={['100%', '100%', '25%', '25%']}
                            fontSize="14px"
                        >
                            <Box
                                onClick={rejectAll}
                                cursor={canTakeAction ? 'pointer' : 'not-allowed'}
                                opacity={canTakeAction ? '1' : '0.3'}
                            >
                                <Text px="10px" color="var(--chakra-colors-status-error)">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.rejectAll')}
                                </Text>
                            </Box>
                            <Box
                                onClick={approveAll}
                                cursor={canTakeAction ? 'pointer' : 'not-allowed'}
                                opacity={canTakeAction ? '1' : '0.3'}
                            >
                                <Text px="10px">{t('tasksApprovals.sapGrcAccessRequestDrawer.body.approveAll')}</Text>
                            </Box>
                        </Flex>
                    </Flex>
                    {isRejectAll && (
                        <Box>
                            <Textarea
                                variant="gsk"
                                placeholder="Reason for rejecting all *"
                                style={{ caretColor: 'var(--chakra-colors-orange-400)' }}
                                value={rejectReasonForAll}
                                py="18px"
                                rows={1}
                                onChange={(e) => setRejectReasonForAll(e.currentTarget.value)}
                            />
                        </Box>
                    )}
                </Box>
                {childItems.map((item) => (
                    <ChildItem
                        key={item.approval_entity_id}
                        item={item}
                        onApprove={onApprove}
                        onReject={onReject}
                        isRejectAll={isRejectAll}
                        setRejectReason={setRejectReason}
                        rejectReason={item.rejectReason}
                    />
                ))}
            </Box>
        )
    )
}

export default Container
